import { useCallback, useMemo, useRef } from 'react';

import type { Timeout } from './types';

export type TimedLoop = {
  start: () => void,
  current: Timeout | null,
  isRunning: boolean,
  stop: () => void,
};

const useTimedLoop = (onLoop: () => void, intervalSec: number): TimedLoop => {
  const idRef = useRef<Timeout | null>(null);

  const loop = useCallback(() => {
    idRef.current = setTimeout(() => {
      onLoop();
      loop();
    }, intervalSec * 1000);
  }, [onLoop, intervalSec]);

  const result = useMemo(() => ({
    start: () => {
      if (idRef.current === null) {
        loop();
      }
    },
    current: idRef.current,
    isRunning: idRef.current !== null,
    stop: () => {
      if (idRef.current !== null) {
        clearTimeout(idRef.current);
        idRef.current = null;
      }
    },
  }), [loop]);

  return result;
};

export default useTimedLoop;
